import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './shared/guards/auth.guard';
import { onboardingGuard, completedOnboardingGuard } from './shared/guards/onboarding.guard';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { OnboardingComponent } from './pages/onboarding/onboarding.component';
import { LoginComponent } from './pages/auth/login/login.component';
import { RegisterComponent } from './pages/auth/register/register.component';
import { ForgotPasswordComponent } from './pages/auth/forgot-password/forgot-password.component';
import { AccountComponent } from './pages/settings/account/account.component';
import { ProfileComponent } from './pages/settings/profile/profile.component';
import { BillingProfileComponent } from './pages/settings/billing-profile/billing-profile.component';
import { ResetPasswordComponent } from './pages/auth/reset-password/reset-password.component';
import { LinkedinComponent } from './pages/auth/oauth/linkedin/linkedin.component';
import { AccountSetupComponent } from './pages/auth/account-setup/account-setup.component';
import { HubspotComponent } from './pages/auth/oauth/hubspot/hubspot.component';
import { GoogleComponent } from './pages/auth/oauth/google/google.component';
import { ReportComponent } from './pages/report/report.component';
import { GdprNoticeComponent } from './pages/gdpr-notice/gdpr-notice.component';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';
import { AccountCancellationSuccessfulComponent } from './pages/settings/account/account-cancellation-successful/account-cancellation-successful.component';
import { UpdateAccountSetupComponent } from './pages/settings/update-account-setup/update-account-setup.component';
import { InvoicesComponent } from './pages/settings/invoices/invoices.component';
import { AccountLayoutComponent } from './layouts/account-layout/account-layout.component';
import { PublicProfileComponent } from './pages/public-profile/public-profile.component';
import { ReportsComponent } from './pages/reports/reports.component';
import { PsyTestingComponent } from './pages/psy-testing/psy-testing.component';
import { PsyTestingReportComponent } from './pages/psy-testing/psy-testing-report/psy-testing-report.component';
import { AdvancedSearchComponent } from './pages/advanced-search/advanced-search.component';
import { CompaniesComponent } from './pages/advanced-search/companies/companies.component';
import { PeopleComponent } from './pages/advanced-search/companies/people/people.component';
import {MainLayoutComponent} from "./layouts/main-layout/main-layout.component";
import {IcpOptimizationComponent} from "./pages/icp-optimization/icp-optimization.component";
import {IcpComponent} from "./pages/icp/icp.component";
import {IcpCreateComponent} from "./pages/icp/create/icp-create.component";
import {IcpEditComponent} from "./pages/icp/edit/icp-edit.component";
import {IcpDetailComponent} from "./pages/icp/detail/icp-detail.component";
import {SettingsLayoutComponent} from "./layouts/settings-layout/settings-layout.component";
import {WorkspacesComponent} from "./pages/workspaces/workspaces.component";
import {WorkspaceComponent} from "./pages/workspace/workspace.component";
import {CrmComponent} from "./pages/crm/crm.component";
import {MarketInsightComponent} from "./pages/market-insight/market-insight.component";
import {MarketIntelligenceReportComponent} from "./pages/market-intelligence-report/market-intelligence-report.component";
import {BillingComponent} from "./pages/billing/billing.component";
import {ExportsComponent} from "./pages/exports/exports.component";
import {LeadsLegacyComponent} from "./pages/leads-legacy/leads-legacy.component";
import {LeadsComponent} from "./pages/leads/leads.component";
import { UiHandoffComponent } from './pages/auth/ui-handoff/ui-handoff.component';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'login/linkedin',
    component: LinkedinComponent,
  },
  {
    // New UI migration: switching back from the new UI lands here (no guards).
    path: 'auth/handoff',
    component: UiHandoffComponent,
  },
  {
    path: 'register',
    component: RegisterComponent,
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
  },
  {
    path: 'onboarding',
    component: OnboardingComponent,
    canActivate: [AuthGuard, completedOnboardingGuard],
  },

  // Guarded routes
  /*
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [AuthGuard],
  },
  */
  // ICP routes (moved outside to avoid conflict with profile routes)
  { path: 'icp', component: MainLayoutComponent, canActivate: [AuthGuard, onboardingGuard], children: [
    { path: '', component: IcpComponent },
    { path: 'create', component: IcpCreateComponent },
    { path: 'detail/:id', component: IcpDetailComponent },
    { path: 'edit/:id', component: IcpEditComponent }
  ]},

  {
    path: '',
    component: MainLayoutComponent,
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', component: DashboardComponent, canActivate: [AuthGuard, onboardingGuard] },
      { path: 'reports', component: ReportsComponent, canActivate: [AuthGuard, onboardingGuard] },
      { path: 'workspaces', component: WorkspacesComponent, canActivate: [AuthGuard, onboardingGuard] },
      { path: 'workspaces/:id', component: WorkspaceComponent, canActivate: [AuthGuard, onboardingGuard] },
      { path: 'billing', component: BillingComponent, canActivate: [AuthGuard, onboardingGuard] },
      { path: 'icp-optimization', redirectTo: '/icp/create', pathMatch: 'full' }, // Redirect old route to new create
      { path: 'crm', component: CrmComponent, canActivate: [AuthGuard, onboardingGuard] },
      { path: 'psy-testing',  component: PsyTestingComponent, canActivate: [AuthGuard, onboardingGuard] },
      { path: 'psy-testing-report', component: PsyTestingReportComponent, canActivate: [AuthGuard, onboardingGuard] },
      { path: 'advanced-search', component: AdvancedSearchComponent, canActivate: [AuthGuard, onboardingGuard] },
      { path: 'advanced-search/companies', component: CompaniesComponent, canActivate: [AuthGuard, onboardingGuard] },
      { path: 'advanced-search/companies/:companyId/people', component: PeopleComponent, canActivate: [AuthGuard, onboardingGuard] },
      { path: 'leads-legacy', component: LeadsLegacyComponent, canActivate: [AuthGuard, onboardingGuard] },
      // Simplified workspace-scoped Leads page (replaces the pipeline UI).
      { path: 'leads', component: LeadsComponent, canActivate: [AuthGuard, onboardingGuard] },
      // Lead pipelines are hidden from the UI (backend + code kept): the old
      // URL redirects to the simplified page. Restore the loadChildren block
      // below to bring the pipeline pages back.
      { path: 'leads/pipelines', redirectTo: 'leads', pathMatch: 'prefix' },
      // {
      //   path: 'leads/pipelines',
      //   loadChildren: () =>
      //     import('./pages/lead-pipelines/lead-pipelines.module').then(m => m.LeadPipelinesModule),
      //   canActivate: [AuthGuard, onboardingGuard],
      // },
      { path: 'exports', component: ExportsComponent, canActivate: [AuthGuard, onboardingGuard] }
    ]
  },

  /*
  {
    path: 'reports',
    component: ReportsComponent,
    canActivate: [AuthGuard],
  },
  */

  {
    path: 'report/:id',
    component: ReportComponent,
    canActivate: [AuthGuard, onboardingGuard],
  },

  {
    path: 'market-insight',
    component: MarketInsightComponent,
    canActivate: [AuthGuard, onboardingGuard],
  },

  /*
  {
    path: 'registration/setup',
    component: AccountSetupComponent,
    canActivate: [AuthGuard],
  },
  */

  /*
  {
    path: "settings/account/setup",
    component: UpdateAccountSetupComponent,
    canActivate: [AuthGuard],
  },
  */

  {
    path: 'settings/account/hubspot',
    component: HubspotComponent,
    canActivate: [AuthGuard, onboardingGuard],
  },
  {
    path: 'settings/account/google',
    component: GoogleComponent,
    canActivate: [AuthGuard, onboardingGuard],
  },

  {
    path: 'settings',
    component: SettingsLayoutComponent,
    children: [
      {
        path: 'account',
        component: AccountComponent,
        canActivate: [AuthGuard, onboardingGuard],
      },
      {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [AuthGuard, onboardingGuard],
      },
      {
        path: 'billing-profile',
        component: BillingProfileComponent,
        canActivate: [AuthGuard, onboardingGuard],
      },
      {
        path: 'invoices',
        component: InvoicesComponent,
        canActivate: [AuthGuard, onboardingGuard],
      },
      /*
      {
        path: 'account-setup',
        component: UpdateAccountSetupComponent,
        canActivate: [AuthGuard],
      },
     */
    ],
  },

  /*
  {
    path: "settings/account",
    component: AccountComponent,
    canActivate: [AuthGuard],
  },
  {
    path:"settings/profile",
    component:ProfileComponent,
    canActivate:[AuthGuard],
  },
  {
    path:"settings/billing-profile",
    component:BillingProfileComponent,
    canActivate:[AuthGuard],
  },
  {
    path:"settings/invoices",
    component:InvoicesComponent,
    canActivate:[AuthGuard],
  },
  */

  {
    path: 'market-intelligence-report',
    component: MarketIntelligenceReportComponent,
  },
  {
    path: 'gdpr-notice',
    component: GdprNoticeComponent,
  },
  {
    path: 'account-cancellation-successful',
    component: AccountCancellationSuccessfulComponent,
  },

  {
    path: '404',
    component: PageNotFoundComponent,
  },
  {
    path: ':name-profile-id',
    component: PublicProfileComponent,
  },
  { path: '**', component: PageNotFoundComponent },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
